import { useEffect } from 'react'

import Footer from 'components/Footer'
import Header from 'components/Header'
import SideNav from 'components/Header/SideNav'
import IdleTimeout from 'components/ui/IdleTimeout'
import WindowClose from 'components/ui/WindowClose'
import DashboardLoader from 'components/DashboardLoader'
import PrivateRoutes from 'PrivateRoutes'
import PublicRoutes from 'PublicRoutes'
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom'
import { Slide, ToastContainer, toast } from 'react-toastify'
import { AuthContext, useCognito } from 'services/amplify'
import useAuthStore from 'store/auth'
import { SWRConfig } from 'swr'
import { logout } from 'utils/logout'
import { QueryClient, QueryClientProvider } from 'react-query'
import { setLogoutContext } from 'utils/http'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false },
    mutations: {},
  },
})

function QueryProvider({ children }: { children: React.ReactNode }) {
  const cognito = useCognito()
  const navigate = useNavigate()

  useEffect(() => {
    setLogoutContext(navigate, cognito)
  }, [navigate, cognito])

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (!event?.query) return

      const error = event.query.state.error as
        | { outcomeUserMessage?: string }
        | undefined
      if (error?.outcomeUserMessage?.includes('Access Token has expired')) {
        toast.error('Session expired, please login again.')
        logout(navigate, cognito)
      }
    })

    return () => unsubscribe()
  }, [navigate, cognito])

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  )
}

const App = (): JSX.Element => {
  const user = useAuthStore((state) => state.user)
  const attributes = useAuthStore((state) => state.attributes)
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn)
  const isLoading = useAuthStore((state) => state.isLoading)
  const location = useLocation()
  const isHDAReporstRoute = location.pathname.split('/').includes('reports')

  // console.log(attributes, ' Attributes ...')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-150">
        <DashboardLoader />
      </div>
    )
  }

  if (!isLoggedIn && !user) {
    return <PublicRoutes />
  }

  if (isLoggedIn && user && isHDAReporstRoute) {
    return (
      <div className="flex flex-col flex-1 h-full pl-4 pt-2">
        <PrivateRoutes />
      </div>
    )
  }

  return (
    <main className="flex flex-col min-w-full min-h-screen bg-gray-150">
      <WindowClose />
      <IdleTimeout />
      <Header />
      <SideNav />

      <div className="flex flex-col flex-1 h-full p-6 lg:ml-20 lg:mt-14">
        <PrivateRoutes />
      </div>
      <Footer />
    </main>
  )
}

const Main = (): JSX.Element => {
  const cognito = useCognito()
  return (
    <AuthContext.Provider value={cognito}>
      <SWRConfig
        value={{
          errorRetryCount: 5,
          revalidateOnFocus: false,
        }}
      >
        <ToastContainer
          autoClose={5_000}
          position="bottom-center"
          transition={Slide}
          draggable={false}
          hideProgressBar
        />

        <BrowserRouter>
          <QueryProvider>
            <App />
          </QueryProvider>
        </BrowserRouter>
      </SWRConfig>
    </AuthContext.Provider>
  )
}

export default Main
